import { useLazyQuery, useMutation } from '@apollo/client'
import { usePrompt, useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { formatISO } from 'date-fns'
import { useRef, useState } from 'react'
import { CREATE_TIME_ENTRY_MUTATION } from '@/modules/timesheets/mutations'
import { useTimeEntryErrorToast } from '@/modules/timesheets/time-entry-errors.hooks'
import type { ApproveWorkPeriodNode } from './types.graphql'
import { EXTEND_USER_SALARY_WORK_TIME } from './queries'
import {
  ExtendWorkPeriodModal,
  type ExtendWorkPeriodFormValues,
} from './extend-work-period-modal'

type UseWorkPeriodDayExtensionActionsArgs = {
  initRefetch: () => void
}

export const useWorkPeriodDayExtensionActions = ({
  initRefetch,
}: UseWorkPeriodDayExtensionActionsArgs) => {
  const addPrompt = usePrompt()
  const addToast = useToast()
  const showTimeEntryError = useTimeEntryErrorToast()
  const extendDayFlowOpenRef = useRef(false)
  const [extendDayFlowOpen, setExtendDayFlowOpen] = useState(false)
  const translations = useTranslate({
    extendWorkPeriod: 'salary.extend-work-period',
    beforeDay: 'salary.extend-work-period-before',
    afterDay: 'salary.extend-work-period-after',
    success: 'timesheets.success-registration-timeentry',
    error: {
      create: 'timesheets.error-create-timeentry',
      commentRequired: 'timesheets.error.comment-required',
    },
    warning: {
      create: 'timesheets.warning.time-entry-exist-in-daterange',
    },
  })

  const [createTimeEntry, { loading }] = useMutation(CREATE_TIME_ENTRY_MUTATION)
  const [fetchUserSalaryWorkTime, { loading: loadingSalaryWorkTime }] =
    useLazyQuery(EXTEND_USER_SALARY_WORK_TIME)

  const showCreateErrorToast = (message: string) => {
    if (showTimeEntryError(message)) return

    addToast('error', message || translations.error.create)
  }

  const getEntryComment = (
    entry: ExtendWorkPeriodFormValues['entries'][number],
    comment: string
  ) => {
    const prefix = `${translations.extendWorkPeriod}: ${
      entry.side === 'before' ? translations.beforeDay : translations.afterDay
    }`

    return comment ? `${prefix} - ${comment}` : prefix
  }

  const openExtendDayFlow = async (workPeriod: ApproveWorkPeriodNode) => {
    if (extendDayFlowOpenRef.current) return

    // The approval list only carries the salaryWorkTime id (for gating this
    // button); the department lists the modal needs are fetched here on demand.
    if (!workPeriod.user.salaryWorkTime || workPeriod.approvedByManager) return

    extendDayFlowOpenRef.current = true
    setExtendDayFlowOpen(true)

    let shouldRefetch = false

    try {
      const { data: salaryWorkTimeData } = await fetchUserSalaryWorkTime({
        variables: { userId: workPeriod.user.id },
      })
      const salaryWorkTime = salaryWorkTimeData?.user?.salaryWorkTime
      if (!salaryWorkTime) return

      const { data } = await addPrompt<ExtendWorkPeriodFormValues | null>(
        resolve => (
          <ExtendWorkPeriodModal
            onResolve={resolve}
            salaryWorkTime={salaryWorkTime}
            workPeriod={workPeriod}
          />
        )
      )

      if (!data) return

      shouldRefetch = true

      const results = await Promise.allSettled(
        data.entries.map(entry =>
          createTimeEntry({
            variables: {
              input: {
                user: data.userId,
                datetimeStart: formatISO(entry.start),
                datetimeEnd: formatISO(entry.end),
                facility: data.facility,
                comment: getEntryComment(entry, data.comment),
                salaryCodeOrdinary: data.salaryCodeId,
                department: data.department,
              },
            },
          })
        )
      )

      const successfulResults = results.filter(
        result => result.status === 'fulfilled'
      )
      const rejectedMessages = [
        ...new Set(
          results.flatMap(result =>
            result.status === 'rejected'
              ? [
                  result.reason instanceof Error
                    ? result.reason.message
                    : translations.error.create,
                ]
              : []
          )
        ),
      ]

      if (successfulResults.length > 0) {
        addToast('success', translations.success)
      }

      for (const message of rejectedMessages) {
        showCreateErrorToast(message)
      }
    } finally {
      if (shouldRefetch) {
        initRefetch()
      }

      extendDayFlowOpenRef.current = false
      setExtendDayFlowOpen(false)
    }
  }

  return {
    creatingExtension: loading || loadingSalaryWorkTime || extendDayFlowOpen,
    openExtendDayFlow,
  }
}
